import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    cloneElement,
    useLayoutEffect,
    useImperativeHandle
} from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';

import { UpdateUserProfileDocument } from '@zoopla/user-queries';
import { checkResponseType } from './helper/checkResponseType';
import {
    YourSituationProps,
    QuestionNames,
    AnswerData
} from './your-situation.types';
import { Controller, useForm } from 'react-hook-form';
import { SavedQuestion, BasicQuestion } from '../question';
import {
    trackQuestionEdit,
    trackQuestionError,
    trackQuestionSaved,
    trackQuestionShown
} from './your-situation.tracking';
import { buttonContainerStyle } from './your-situation.styles.css';

const getInitialSteps = ({
    firstQuestionId,
    initialValues
}: Pick<YourSituationProps, 'initialValues' | 'firstQuestionId'>) => {
    if (!initialValues) {
        return [firstQuestionId];
    }

    const initialList = Object.keys(initialValues).filter(
        (item) =>
            initialValues[item] !== null && initialValues[item] !== undefined
    );

    return initialList.length === 0 ? [firstQuestionId] : initialList;
};

type GetStepsArg = {
    questionsMap: YourSituationProps['questionsMap'];
    currentSteps: string[];
};

type GetNextStepsArg = {
    formData: AnswerData;
    firstQuestionId: string;
} & GetStepsArg;

const getStepsWithNext = ({
    formData,
    questionsMap,
    currentSteps,
    firstQuestionId
}: GetNextStepsArg) => {
    const lastQuestion = currentSteps[currentSteps.length - 1];

    if (!isQuestionName<QuestionNames>(lastQuestion, questionsMap)) {
        throw new Error();
    }

    const nextQuestion = questionsMap[lastQuestion].getNext(
        formData[lastQuestion] || '',
        formData[firstQuestionId] || ''
    );

    return [...currentSteps, nextQuestion];
};

type GetStepsToQuestionArg = {
    currentStepName: string;
} & GetStepsArg;

const getStepsToQuestion = ({
    currentStepName,
    questionsMap,
    currentSteps
}: GetStepsToQuestionArg) => {
    if (!isQuestionName<QuestionNames>(currentStepName, questionsMap)) {
        throw new Error();
    }

    // // removes other questions after current question in the list
    const currentQuestionIndex = currentSteps.findIndex(
        (item) => item === currentStepName
    );
    const newCurrentSteps = currentSteps.filter(
        (_item, index) => index <= currentQuestionIndex
    );

    return newCurrentSteps;
};

const isQuestionName = <T extends string>(
    question: string,
    questionsMap: Record<T, unknown>
): question is T => {
    return Object.keys(questionsMap).some((key) => key === question);
};

type FormFocusElement = { focus: () => void };

export const YourSituation = forwardRef<FormFocusElement, YourSituationProps>(
    (
        {
            analyticsPage,
            questionsMap,
            firstQuestionId,
            initialValues,
            actionAfterQuestionsElement,
            saveData: passedSavedData,
            forceFormMode,
            onResults,
            onFirstQuestionSaved,
            showEditButton,
            initialLoadAsDone = false,
            trackingIdentifier = ''
        },
        ref
    ) => {
        const isInitialMountRef = useRef(true);
        const {
            control,
            handleSubmit,
            formState: { errors },
            reset,
            clearErrors,
            setFocus,
            getValues
        } = useForm({
            defaultValues: initialValues
        });
        const [isLoading, setIsLoading] = useState(false);

        const [updateAnswer] = useMutation(gql(UpdateUserProfileDocument));

        const [isError, setIsError] = useState(false);
        const [currentSteps, setCurrentSteps] = useState<string[]>(
            getInitialSteps({ firstQuestionId, initialValues })
        );
        const [isDone, setIsDone] = useState(initialLoadAsDone);

        // we are exposing this ref here
        // in order for the parent to focus the last questions
        // as we are already passing the form ref to the steps
        useImperativeHandle(ref, () => ({
            focus: () => setFocus(currentSteps[currentSteps.length - 1])
        }));

        const saveData = async (data: AnswerData) => {
            if (typeof passedSavedData === 'function') {
                return await passedSavedData(data);
            }

            const input = checkResponseType(data);
            return await updateAnswer({
                variables: {
                    input
                }
            });
        };

        const submitForm = handleSubmit(async (data: AnswerData) => {
            setIsError(false);
            setIsLoading(true);

            const lastQuestionName =
                currentSteps[currentSteps.length - 1] || '';
            const lastQuestionValue = data[lastQuestionName] || '';
            const isFirstQuestion = currentSteps.length === 1;

            try {
                // update only the field that has been changed
                await saveData({
                    [lastQuestionName]: lastQuestionValue
                });

                const newSteps = getStepsWithNext({
                    formData: data,
                    questionsMap,
                    currentSteps,
                    firstQuestionId
                });

                trackQuestionSaved(
                    lastQuestionName,
                    lastQuestionValue,
                    analyticsPage,
                    trackingIdentifier
                );

                if (isFirstQuestion) {
                    onFirstQuestionSaved?.();
                }

                // we've reached the end of the steps
                if (newSteps[newSteps.length - 1] === 'done') {
                    onResults?.(newSteps);

                    setIsDone(true);
                    return;
                }

                setCurrentSteps(newSteps);

                const nextQuestion = newSteps[newSteps.length - 1];
                // type guard more than anything, we always expect there to be a next step
                if (nextQuestion) {
                    trackQuestionShown(
                        nextQuestion,
                        analyticsPage,
                        trackingIdentifier
                    );
                }
            } catch {
                setIsError(true);

                setTimeout(() => {
                    setFocus(lastQuestionName);
                }, 200);

                trackQuestionError(
                    lastQuestionName,
                    lastQuestionValue,
                    analyticsPage,
                    trackingIdentifier
                );
            } finally {
                setIsLoading(false);
            }
        });

        const onEditQuestion = (currentStepName: string) => {
            setIsDone(false);
            setIsError(false);

            const data = getValues();
            const lastQuestionName =
                currentSteps[currentSteps.length - 1] || '';
            const lastQuestionValue = data[lastQuestionName] || '';

            const newSteps = getStepsToQuestion({
                questionsMap,
                currentSteps,
                currentStepName
            });

            trackQuestionEdit(
                lastQuestionName,
                lastQuestionValue,
                analyticsPage,
                trackingIdentifier
            );

            setCurrentSteps(newSteps);
        };

        const forceEditFullForm = () => {
            setIsDone(false);
            setCurrentSteps([firstQuestionId]);
        };

        const forceResetFullForm = () => {
            reset({});
            setIsDone(false);
            setCurrentSteps([firstQuestionId]);
        };

        // allow parents to force the form to show as fillable (non-saved)
        // or to reset all the data
        useEffect(() => {
            if (forceFormMode === 'force_edit') {
                forceEditFullForm();
            } else if (forceFormMode === 'force_reset') {
                forceResetFullForm();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [forceFormMode]);

        // track questions shown initially
        useEffect(() => {
            const initialStepsArray = getInitialSteps({
                firstQuestionId,
                initialValues
            });

            initialStepsArray.forEach((step) => {
                trackQuestionShown(step, analyticsPage, trackingIdentifier);
            });

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // set focus when each new step is shown
        useLayoutEffect(() => {
            if (isInitialMountRef.current) {
                return () => {
                    // make sure we set any re-renders as not being the initial mount effect
                    isInitialMountRef.current = false;
                };
            }

            const lastQuestionName =
                currentSteps[currentSteps.length - 1] || '';

            if (lastQuestionName === 'done') {
                return;
            }

            const focusTimeout = setTimeout(() => {
                setFocus(lastQuestionName);
            }, 200);

            return () => {
                clearTimeout(focusTimeout);
            };
        }, [currentSteps, setFocus]);

        const doneElement = actionAfterQuestionsElement
            ? cloneElement(actionAfterQuestionsElement, {
                  goal: getValues()[firstQuestionId],
                  lastQuestionId: currentSteps[currentSteps.length - 1] || ''
              })
            : null;

        return (
            <form onSubmit={submitForm}>
                {currentSteps.map((step) => {
                    if (!isQuestionName<QuestionNames>(step, questionsMap)) {
                        return null;
                    }

                    const { options, ...question } = questionsMap[step];
                    const isCurrentQuestion =
                        currentSteps[currentSteps.length - 1] === step;

                    return (
                        <Box key={step} pb="S400">
                            <Controller
                                name={step}
                                control={control}
                                render={({
                                    field: { onChange, ref, value }
                                }) => {
                                    const hasValue = !!value;
                                    const isSaved =
                                        (hasValue && !isCurrentQuestion) ||
                                        isDone;

                                    const selectedOption = options.find(
                                        (option) => option.value === value
                                    );

                                    if (isSaved) {
                                        const text = question.formatAnswer({
                                            label: selectedOption?.label || '',
                                            value: value || ''
                                        });

                                        return (
                                            <SavedQuestion
                                                text={text}
                                                questionLabel={question.label}
                                                id={`question-${step}-label`}
                                                testId={`answer-${step}`}
                                                editButton={
                                                    showEditButton ? (
                                                        <Button
                                                            category="tertiary"
                                                            onClick={() =>
                                                                onEditQuestion(
                                                                    step
                                                                )
                                                            }
                                                            ref={ref}
                                                            aria-label={`Edit ${text}`}
                                                            type="button"
                                                            aria-describedby={`question-${step}-label`}
                                                        >
                                                            Edit
                                                        </Button>
                                                    ) : undefined
                                                }
                                            />
                                        );
                                    }

                                    const optionsWithError = options.map(
                                        (option, idx) => {
                                            if (
                                                isError ||
                                                Object.keys(errors)?.length > 0
                                            ) {
                                                if (hasValue) {
                                                    if (
                                                        option.value === value
                                                    ) {
                                                        return {
                                                            ...option,
                                                            errorText:
                                                                'Something went wrong.'
                                                        };
                                                    }

                                                    return option;
                                                } else if (idx === 0) {
                                                    return {
                                                        ...option,
                                                        errorText:
                                                            'Select an option'
                                                    };
                                                }
                                            }

                                            return option;
                                        }
                                    );

                                    return (
                                        <BasicQuestion
                                            onChange={(args) => {
                                                setIsError(false);
                                                clearErrors();
                                                onChange(args);
                                            }}
                                            id={`question-${step}`}
                                            ref={ref}
                                            selectedValue={value || undefined}
                                            ariaDescribedBy="question-error"
                                            options={optionsWithError}
                                            {...question}
                                        />
                                    );
                                }}
                                rules={{ required: true }}
                            />
                        </Box>
                    );
                })}

                {isDone ? (
                    doneElement
                ) : (
                    <div className={buttonContainerStyle}>
                        <Button
                            category="primary"
                            type="submit"
                            loading={isLoading}
                            loadingText="Submitting"
                            fw
                        >
                            Next
                        </Button>
                    </div>
                )}
            </form>
        );
    }
);
